import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ActionScope,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { DatasetModel } from './dataset.model';
import { DATASET_CUSTOM_QUERY_TABLE } from './preview/source-sql';

const runDatasetPreviewQueryParamsSchema = z.object({
  /**
   * What is in the query box. Omitted means "run what is already applied",
   * which is what the page-level shortcut and the command palette pass.
   *
   * Described rather than only commented: this schema is also the tool contract
   * Coco sees, so the guidance has to reach the model.
   */
  query: z
    .string()
    .optional()
    .describe(
      `A SELECT statement against the table \`${DATASET_CUSTOM_QUERY_TABLE}\`, e.g. ` +
        `\`SELECT * FROM ${DATASET_CUSTOM_QUERY_TABLE} LIMIT 10\`. Only SELECT is ` +
        'supported. Omit to re-run the query already applied. Running also ' +
        'applies the query to what the user is looking at.'
    ),
  /**
   * Which dataset to run against. Omitted means the targeted one, which is what
   * every on-page caller wants; callers with no page — Coco — name it, because
   * several dataset previews can be mounted at once and only one of them is
   * the one being talked about.
   */
  datasetId: z
    .string()
    .optional()
    .describe(
      'Id of the dataset to query, as named in this tool description. Omit to use whichever the user is looking at.'
    ),
});

type Params = z.infer<typeof runDatasetPreviewQueryParamsSchema>;

/**
 * Applies the preview query to the dataset grid.
 *
 * Running is not saving: the query takes effect immediately and stays unsaved
 * until {@link SaveDatasetAction} persists it, which is why running an edited
 * query adopts it rather than leaving the page's idea of the query behind.
 */
export class RunDatasetPreviewQueryAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  /** Stable across minification, and the id Coco calls it by. */
  static readonly actionId = 'run-dataset-preview-query';

  title = 'Run preview query';
  description = 'Run the dataset preview query';
  inputSchema = runDatasetPreviewQueryParamsSchema;
  shortcut = ['ctrl', 'enter'];
  group = 'Dataset';
  icon = 'play';

  /**
   * Offered to Coco: read-only by construction — the preview rejects anything
   * that is not a SELECT — so it runs without asking, and it hands back a
   * sample so the model can read what it asked for rather than only knowing
   * that it ran.
   */
  scopes: ActionScope[] = ['chat'];

  describeForAgent(context: ApplicableContext): string | null {
    const open = context
      .getInScopeModelsOfType(DatasetModel)
      .filter((model) => model.canRunPreviewQuery);
    if (open.length === 0) {
      return null;
    }
    const named = open
      .map((model) => `"${model.name}" (datasetId: ${model.datasetId})`)
      .join(', ');
    return (
      'Run a read-only SQL SELECT against a dataset preview the user has open, and get ' +
      `row count, column names and up to 10 sample rows back. Currently open: ${named}. ` +
      `The dataset is the table \`${DATASET_CUSTOM_QUERY_TABLE}\`.`
    );
  }

  applicable(context: ApplicableContext): boolean {
    // No mounted preview means nothing to run, and Ctrl+Enter is left to
    // whatever else on the page wants it. Any mounted preview counts, not only
    // the targeted one, because a caller naming a dataset can reach the others.
    const targeted = this.getTargetModel(context, undefined);
    if (targeted !== null && targeted.canRunPreviewQuery) {
      return true;
    }
    return context
      .getInScopeModelsOfType(DatasetModel)
      .some((model) => model.canRunPreviewQuery);
  }

  shouldAsk(): boolean {
    return false;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = this.getTargetModel(context, payload.datasetId);
    if (model === null) {
      return err([{ t: 'error', message: 'No dataset in scope' }]);
    }
    const run = model.runPreviewQuery(payload.query);
    if (run === null) {
      return err([{ t: 'error', message: 'No dataset preview to run' }]);
    }
    return ok({ output: await run });
  }

  private getTargetModel(
    context: ApplicableContext,
    datasetId: string | undefined
  ): DatasetModel | null {
    // A named dataset resolves to that dataset or to nothing: a query aimed at
    // one dataset must never quietly answer for another.
    if (datasetId !== undefined && datasetId !== '') {
      return context.getInScopeModelOfType(DatasetModel, datasetId);
    }
    return context.targetedModelOfType(DatasetModel);
  }
}
